<template>
  <div class="apartment-search-full">
    <div class="container">
      <ApartmentSearchForm
        :preset="preset"
        size="full"
        @submit="submit"
      />

      <LoadSpinner
        v-if="loading"
        class="apartment-search-full__loading"
      />

      <template v-else>
        <div
          v-if="submitted"
          class="apartment-search-full__title"
        >
          <template v-if="apartments.length > 0">
            {{translate('Vapaat ja vapautumassa olevat asunnot')}}
            {{sprintf(translate('%s kpl'), apartmentsTotalCount)}}
          </template>
          <template v-else-if="housingLocalities.length > 0">
            {{translate('Haulla ei löytynyt vapaita asuntoja.')}}
          </template>
          <template v-else>
            {{translate('Haulla ei löytynyt tuloksia.')}}
          </template>
        </div>

        <div
          v-if="apartments.length > 0"
          class="apartment-search-full__sorting"
        >
          <label for="sorting" class="input-wrap__label">{{translate('Valitse hakutulosten listautumisjärjestys')}}</label>

          <multiselect
            v-model="sortChoice"
            @select="sortingHandler"
            deselect-label="Can't remove this value"
            track-by="name"
            label="name"
            :options="options"
            :searchable="false"
            :allow-empty="false"
          >
            <template slot="singleLabel" slot-scope="{ option }"><strong>{{ option.name }}{{option.sortChoice}}</strong></template>
          </multiselect>
        </div>

        <div
          v-if="apartments.length > 0"
          class="apartment-search-full__grid listing-card-grid"
        >
          <div class="listing-card-grid__grid">
            <div
              v-for="apartment in apartments"
              class="listing-card-grid__column"
            >

              <ApartmentCard
                :apartment="apartment"
                :get-params="{back: true}"
                class="listing-card-grid__item"
              />
            </div>
          </div>
        </div>

        <div
          v-if="submitted"
          class="apartment-search-full__load-more-container"
        >
          <button
            v-if="!isLastPage"
            :disabled="loadingMore"
            class="apartment-search-full__load-more btn"
            :class="`btn--green`"
            @click="loadMore"
          >
            <LoadSpinner
              v-if="loadingMore"
              :margin="false"
            />
            <template v-else>
              {{translate('Lataa lisää hakutuloksia')}}
            </template>
          </button>
        </div>
      </template>
    </div>

    <div
      class="apartment-search-full__housing-localities"
      v-if="!loading && housingLocalities.length > 0 && this.formCopy.type != 'owner-occupancy'"
    >
      <div class="container">
        <h2 class="h4 h--centered-divider">
          {{translate('Kohteet, joissa hakuasi vastaavia ei vapaana olevia asuntoja')}}
        </h2>

        <div class="apartment-search-full__grid listing-card-grid">
          <div class="listing-card-grid__grid">
            <div
              v-for="housingLocality in housingLocalities"
              class="listing-card-grid__column"
            >
              <HousingLocalityCard
                :housing-locality="housingLocality"
                :get-params="{back: true}"
                class="listing-card-grid__item"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import {sprintf} from 'sprintf-js';

  import globals from '../lib/globals';
  import utils from './mixins/utils';
  import ApartmentSearchForm from './components/ApartmentSearchForm';
  import ApartmentCard from './components/ApartmentCard';
  import HousingLocalityCard from './components/HousingLocalityCard';
  import LoadSpinner from './components/LoadSpinner';
  import Multiselect from 'vue-multiselect'

  export default {
    components: {
      ApartmentSearchForm,
      ApartmentCard,
      HousingLocalityCard,
      LoadSpinner,
      Multiselect,
    },
    mixins: [
      utils.translate,
      utils.hasApartmentUrl
    ],
    props: {
      preset: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        page: 0,
        isLastPage: false,
        // When the form is submitted, we create a copy of its state that is used to show and load more results.
        formCopy: null,
        submitted: false,
        loading: false,
        loadingMore: false,
        apartments: [],
        housingLocalities: [],
        sortChoice: {value: 'created_at,asc', name: this.translate('Vanhimmat ilmoitukset ensin')},
        sortingCriteria: 'created_at',
        sortingDirection: 'desc',
        formType: 'right-of-occupancy',
        apartmentsTotalCount: 0,
        options: [
          { value: "created_at,asc", name: this.translate("Vanhimmat ilmoitukset ensin") },
          { value: "created_at,desc", name: this.translate("Uusimmat ilmoitukset ensin") },
          { value: "mpc_vastike,asc,1", name: this.translate("Edullisimmat ensin (vastike)") },
          { value: "mpc_vastike,desc,1", name: this.translate("Kalleimmat ensin (vastike)") },
          { value: "mpc_pinta_ala,asc,1", name: this.translate("Pienimmät ensin") },
          { value: "mpc_pinta_ala,desc,1", name: this.translate("Suurimmat ensin") },
          { value: "housing_locality.mpc_valmistumispaiva,asc,2", name: this.translate("Vanhimmat ensin (rakennusvuosi)") },
          { value: "housing_locality.mpc_valmistumispaiva,desc,2", name: this.translate("Uusimmat ensin (rakennusvuosi)") },
        ],
      };
    },
    computed: {
      color() {
        const colors = {
          'rental': 'green',
          'right-of-occupancy': 'green',
          'owner-occupancy': 'green',
        };
        const type = this.formCopy && this.formCopy.type;
        return colors.hasOwnProperty(type) ? colors[type] : 'default';
      },
    },
    methods: {
      sprintf,
      async submit(form) {
        this.submitted = true;
        this.formCopy = form;
        this.formCopy.sortingCriteria = this.sortingCriteria;
        this.formCopy.sortingDirection = this.sortingDirection;
        this.page = 0;
        this.isLastPage = false;
        this.loading = true;
        this.updatePriceOptions(this.formCopy.type);

        const url = `${globals.urlIntegrationApi}/public/apartments-filter-search`;
        const response = await axios.post(url, form);

        //this.apartments = response.data.apartments;
        this.apartments = response.data.apartments.filter(apartment => {
          return this.hasApartmentUrl(apartment); // replace this condition with your own
        });
        this.apartmentsTotalCount = response.data.totalCount;
        this.housingLocalities = response.data.housing_localities;
        this.isLastPage = response.data.last;
        this.loading = false;
      },
      async loadMore() {
        this.page = this.page + 1;
        this.loadingMore = true;

        const url = `${globals.urlIntegrationApi}/public/apartments-filter-search`;
        const response = await axios.post(url, {...this.formCopy, page: this.page});

        this.apartments = [...this.apartments, ...response.data.apartments];
        this.isLastPage = response.data.last;
        this.loadingMore = false;
      },
      updatePriceOptions(formType) {
        // If form type is correct, options will not be mutated.
        if (formType === this.formType) {
          return;
        }

        // Update rental status.
        this.formType = formType;

        // Options array items [3] and [4] contain price options.
        if (formType === 'rental') {
          this.options[3] = { value: "mpc_vuokra,asc,1", name: "Edullisimmat ensin (vuokra)" }
          this.options[4] = { value: "mpc_vuokra,desc,1", name: "Kalleimmat ensin (vuokra)" }
        } else if (formType === 'owner-occupancy') {
          this.options[3] = { value: "ava_Velatonhinta,asc,1", name: "Edullisimmat ensin (velaton hinta)" }
          this.options[4] = { value: "ava_Velatonhinta,desc,1", name: "Kalleimmat ensin (velaton hinta)" }
        } else {
          this.options[3] = { value: "mpc_vastike,asc,1", name: "Edullisimmat ensin (vastike)" }
          this.options[4] = { value: "mpc_vastike,desc,1", name: "Kalleimmat ensin (vastike)" }
        }
        return;
      },
      sortingHandler(sortChoice) {

        // This function handles dropdown changes.
        this.sortingCriteria = sortChoice.value.split(',')[0];
        this.sortingDirection = sortChoice.value.split(',')[1];

        // Use quick sorting when all apartments are loaded.
        if (this.isLastPage) {
          this.sortApartments(sortChoice);
          return;
        }

        this.formCopy.sortingCriteria = this.sortingCriteria;
        this.formCopy.sortingDirection = this.sortingDirection;
        this.submit(this.formCopy);
        return;
      },
      sortApartments(sortChoice) {
        // Options have property name, sorting direction and optional
        // flags for parsing.
        let criteria = sortChoice.value.split(',')[0];
        let direction = sortChoice.value.split(',')[1];

        // Flags:
        // 0 - no parsing
        // 1 - parse float
        // 2 - property is nested
        // 3 - property may contain null
        let flag = parseInt(sortChoice.value.split(',')[2]) || 0;

        // Sorting logic switch.
        switch (flag) {
          case 0:
            this.apartments = [...this.apartments
                .sort((apartment1, apartment2) =>
                apartment1[criteria] > apartment2[criteria] ? 1 : -1)];
            break;

          case 1:
            this.apartments = [...this.apartments
                .sort((apartment1, apartment2) =>
                parseFloat(apartment1[criteria]) > parseFloat(apartment2[criteria]) ? 1 : -1)];
            break;

          case 2:
            this.apartments = [...this.apartments
                .sort((apartment1, apartment2) =>
                apartment1.housing_locality.mpc_valmistumispaiva > apartment2.housing_locality.mpc_valmistumispaiva ? 1 : -1)];
            break;

          case 3:
            let showcaseApartments = [...this.apartments];
            showcaseApartments = this.handleShowcaseApartments(showcaseApartments, criteria);
            this.apartments = showcaseApartments;
            break;

          default:
            break;
        }

        if (direction === 'desc') {
          this.apartments = this.apartments.reverse();
        }

        return;
      },
      // Sort showcase apartments
      handleShowcaseApartments(apartments, criteria) {
        let future = apartments.filter(apartment => Date.parse(apartment[criteria]) > Date.now())
        future.sort((apartment1, apartment2) => apartment1[criteria] > apartment2[criteria] ? 1 : -1);
        let past = apartments.filter(apartment => Date.parse(apartment[criteria]) < Date.now());
        past.sort((apartment1, apartment2) => apartment1[criteria] > apartment2[criteria] ? -1 : 1);
        let nullDates = apartments.filter(apartment => apartment[criteria] === null);

        return future.concat(past).concat(nullDates);
      },
    },
    mounted() {
      if (this.preset) {
        this.submit(this.preset);
      }
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .apartment-search-full {
  }

  .apartment-search-full__loading {
    height: r(128);
  }

  .apartment-search-full__apartments {
    overflow: hidden;
  }

  .apartment-search-full__title {
    @include font-h-28;
    text-align: center;
    margin: r(48) 0 r(24);
  }

  .apartment-search-full__sorting {
    width: 30%;
    min-width: 300px;
    margin: 0 auto 20px;

    select {
      margin: 0 0 r(24) 0;
    }

    option {
      color: $color-black;
    }
  }

  .apartment-search-full__grid {
    margin: 0 0 r(48);
  }

  .apartment-search-full__load-more-container {
    display: flex;
    justify-content: center;
    margin-bottom: r(48);
  }

  .apartment-search-full__load-more {
    min-width: r(256);
  }

  .apartment-search-full__housing-localities {
    overflow: hidden;
    padding-bottom: r(32);
    background-color: $color-white;
  }
</style>
